import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';

export interface PreviewPage {
  pageNumber: number;
  base64Data: string;
}

export interface PreviewMetadata {
  totalPages: number;
  fileType: string;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface PreviewResponse {
  message: string;
  pages: string[];
  total_pages: number;
  current_page: number;
  page_size: number;
  has_next: boolean;
  has_previous: boolean;
  file_type: string;
}

interface CachedArchive {
  metadata: PreviewMetadata;
  pages: Map<number, string>;
  loadedRanges: Array<{ start: number; end: number }>;
}

@Injectable({
  providedIn: 'root'
})
export class PreviewCacheService {
  private cache = new Map<string | number, CachedArchive>();
  private readonly MAX_CACHED_PAGES = 20;
  private readonly PRELOAD_SIZE = 10;

  constructor(private http: HttpClient) {}

  getPreviewPages(
    archiveId: string | number,
    currentPage: number
  ): Observable<{ pages: PreviewPage[]; metadata: PreviewMetadata }> {
    const cached = this.cache.get(archiveId);

    if (cached) {
      const pagesNeeded = this.getPagesForView(currentPage);
      const missingPages = pagesNeeded.filter(p => !cached.pages.has(p));

      if (missingPages.length === 0) {
        const pages = pagesNeeded
          .map(p => ({
            pageNumber: p,
            base64Data: cached.pages.get(p)!
          }))
          .filter(p => p.base64Data);

        return of({ pages, metadata: cached.metadata });
      }
    }

    return this.loadPagesFromServer(archiveId, currentPage);
  }

  loadAllPages(
    archiveId: string | number,
    onProgress?: (loaded: number, total: number) => void
  ): Observable<{ pages: string[]; metadata: PreviewMetadata }> {
    return new Observable(observer => {
      let allPages: string[] = [];
      let finalMetadata: PreviewMetadata | null = null;

      this.getPreviewPages(archiveId, 1).subscribe({
        next: ({ pages, metadata }) => {
          finalMetadata = metadata;
          allPages = pages.map(p => p.base64Data);
          
          observer.next({ pages: allPages, metadata });
          
          if (onProgress) {
            onProgress(allPages.length, metadata.totalPages);
          }

          if (metadata.hasNext) {
            this.loadRemainingPagesInParallel(archiveId, metadata).subscribe({
              next: (newPages) => {
                allPages = [...allPages, ...newPages];
                observer.next({ pages: allPages, metadata: finalMetadata! });
                
                if (onProgress) {
                  onProgress(allPages.length, finalMetadata!.totalPages);
                }
              },
              error: (err) => {
                console.warn('Failed to load some pages:', err);
                observer.complete();
              },
              complete: () => {
                observer.complete();
              }
            });
          } else {
            observer.complete();
          }
        },
        error: (err) => {
          observer.error(err);
        }
      });
    });
  }

  private loadRemainingPagesInParallel(
    archiveId: string | number,
    metadata: PreviewMetadata
  ): Observable<string[]> {
    const totalPages = metadata.totalPages;
    const pageSize = metadata.pageSize;
    const totalRequests = Math.ceil(totalPages / pageSize);
    
    const requests: Observable<{ pages: PreviewPage[]; metadata: PreviewMetadata }>[] = [];
    for (let page = 2; page <= totalRequests; page++) {
      requests.push(this.getPreviewPages(archiveId, page));
    }

    if (requests.length === 0) {
      return of([]);
    }

    return forkJoin(requests).pipe(
      map(results => {
        const allNewPages: string[] = [];
        results.forEach(({ pages }) => {
          allNewPages.push(...pages.map(p => p.base64Data));
        });
        
        if (results.length > 0) {
          const lastMetadata = results[results.length - 1].metadata;
          const cached = this.cache.get(archiveId);
          if (cached) {
            cached.metadata = lastMetadata;
          }
        }
        
        return allNewPages;
      })
    );
  }

  private loadPagesFromServer(
    archiveId: string | number,
    page: number
  ): Observable<{ pages: PreviewPage[]; metadata: PreviewMetadata }> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', this.PRELOAD_SIZE.toString());

    return this.http
      .get<PreviewResponse>(`/api/archives/${archiveId}/preview`, { params })
      .pipe(
        map(response => {
          const metadata: PreviewMetadata = {
            totalPages: response.total_pages,
            fileType: response.file_type,
            currentPage: response.current_page,
            pageSize: response.page_size,
            hasNext: response.has_next,
            hasPrevious: response.has_previous
          };

          const startPage = (response.current_page - 1) * response.page_size + 1;
          const pages: PreviewPage[] = response.pages.map((base64, idx) => ({
            pageNumber: startPage + idx,
            base64Data: `data:image/jpeg;base64,${base64}`
          }));

          this.updateCache(archiveId, pages, metadata);

          return { pages, metadata };
        }),
        catchError(error => {
          throw error;
        })
      );
  }

  private updateCache(
    archiveId: string | number,
    pages: PreviewPage[],
    metadata: PreviewMetadata
  ): void {
    let cached = this.cache.get(archiveId);

    if (!cached) {
      cached = {
        metadata,
        pages: new Map(),
        loadedRanges: []
      };
      this.cache.set(archiveId, cached);
    }

    pages.forEach(page => {
      cached!.pages.set(page.pageNumber, page.base64Data);
    });

    cached.metadata = metadata;

    this.cleanupCache(cached);
  }

  private cleanupCache(cached: CachedArchive): void {
    if (cached.pages.size > this.MAX_CACHED_PAGES) {
      const sortedPages = Array.from(cached.pages.keys()).sort((a, b) => a - b);
      const toRemove = sortedPages.slice(0, sortedPages.length - this.MAX_CACHED_PAGES);
      toRemove.forEach(pageNum => cached.pages.delete(pageNum));
    }
  }


  private getPagesForView(batchPage: number): number[] {
    const pages: number[] = [];
    const startPage = (batchPage - 1) * this.PRELOAD_SIZE + 1;
    const endPage = batchPage * this.PRELOAD_SIZE;

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  getMetadata(archiveId: string | number): PreviewMetadata | null {
    return this.cache.get(archiveId)?.metadata || null;
  }

  clearCache(archiveId: string | number): void {
    this.cache.delete(archiveId);
  }

  clearAllCache(): void {
    this.cache.clear();
  }

  isPageCached(archiveId: string | number, pageNumber: number): boolean {
    return this.cache.get(archiveId)?.pages.has(pageNumber) || false;
  }

  getCacheStats(archiveId: string | number): {
    totalCachedPages: number;
    cacheSize: string;
  } | null {
    const cached = this.cache.get(archiveId);
    if (!cached) return null;

    let totalSize = 0;
    cached.pages.forEach(page => {
      totalSize += page.length;
    });

    return {
      totalCachedPages: cached.pages.size,
      cacheSize: `${(totalSize / 1024 / 1024).toFixed(2)} MB`
    };
  }
}
